// src/pages/calculator/MedicalCalculators.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Pill, Heart, Wind, Scale, Droplets, Calculator, Baby,
  ChevronRight, ArrowLeft, LayoutDashboard, X, AlertTriangle,
  CheckCircle, Info, Loader2, Search, Link as LinkIcon, Activity, Beaker,
} from 'lucide-react';
import { calculatorAPI, medicineAPI, dosageAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import '../../styles/pages.css';

// ── Category config ──────────────────────────────────────────────────────────
const CATEGORIES = [
  {
    label: 'Dose Calculators',
    icon: Pill,
    color: '#1a6ef5',
    bg: '#e8f0fe',
    slugs: ['weight-based-dose', 'bsa-dose', 'loading-dose', 'maintenance-dose', 'infusion-rate'],
  },
  {
    label: 'Renal Calculators',
    icon: Activity,
    color: '#7c3aed',
    bg: '#f5f3ff',
    slugs: ['creatinine-clearance', 'egfr-mdrd', 'gfr'],
  },
  {
    label: 'Cardiology',
    icon: Heart,
    color: '#e11d48',
    bg: '#fff1f2',
    slugs: ['map', 'shock-index'],
  },
  {
    label: 'Respiratory',
    icon: Wind,
    color: '#0891b2',
    bg: '#ecfeff',
    slugs: ['pf-ratio', 'aa-gradient'],
  },
  {
    label: 'Metabolic',
    icon: Beaker,
    color: '#d97706',
    bg: '#fffbeb',
    slugs: ['bmi', 'anion-gap', 'serum-osmolality'],
  },
  {
    label: 'Fluid Calculators',
    icon: Droplets,
    color: '#0284c7',
    bg: '#f0f9ff',
    slugs: ['maintenance-fluid', 'fluid-deficit'],
  },
  {
    label: 'General',
    icon: Scale,
    color: '#059669',
    bg: '#ecfdf5',
    slugs: ['bsa', 'bsa-dubois', 'ibw'],
  },
  {
    label: 'Pregnancy',
    icon: Baby,
    color: '#db2777',
    bg: '#fdf2f8',
    slugs: [
      'edd', 'gestational-age', 'trimester',
      'efetal-weight', 'afi', 'fetal-percentile',
      'bmi-pregnancy', 'pregnancy-weight-gain',
      'down-syndrome-risk', 'preterm-risk', 'gdm-risk',
      'bishop-score', 'contraction-frequency',
      'oxytocin-rate', 'magsulfate-dose', 'iv-fluid-pregnancy',
      'apgar', 'newborn-percentile', 'bilirubin-risk',
    ],
  }
];

// ── Field definitions per calculator slug ────────────────────────────────────
const FIELD_DEFINITIONS = {
  'weight-based-dose': [
    { key: 'weightKg', label: 'Weight', unit: 'kg', type: 'number', step: '0.1' },
    { key: 'dosePerKg', label: 'Dose per kg', unit: 'mg/kg', type: 'number', step: '0.01' },
  ],
  'bsa-dose': [
    { key: 'bsa', label: 'BSA', unit: 'm²', type: 'number', step: '0.01' },
    { key: 'dosePerM2', label: 'Dose per m²', unit: 'mg/m²', type: 'number', step: '0.1' },
  ],
  'loading-dose': [
    { key: 'targetConc', label: 'Target Concentration', unit: 'mg/L', type: 'number', step: '0.01' },
    { key: 'vd', label: 'Volume of Distribution', unit: 'L/kg', type: 'number', step: '0.1' },
    { key: 'bioavailability', label: 'Bioavailability (F)', unit: '0–1', type: 'number', step: '0.01' },
  ],
  'maintenance-dose': [
    { key: 'clearance', label: 'Clearance', unit: 'L/hr', type: 'number', step: '0.01' },
    { key: 'targetConc', label: 'Target Concentration', unit: 'mg/L', type: 'number', step: '0.01' },
    { key: 'time', label: 'Dosing Interval', unit: 'hr', type: 'number', step: '1' },
  ],
  'infusion-rate': [
    { key: 'totalDose', label: 'Total Dose', unit: 'mg', type: 'number', step: '0.1' },
    { key: 'time', label: 'Time', unit: 'hr', type: 'number', step: '0.5' },
  ],
  'creatinine-clearance': [
    { key: 'age', label: 'Age', unit: 'years', type: 'number', step: '1' },
    { key: 'weight', label: 'Weight', unit: 'kg', type: 'number', step: '0.1' },
    { key: 'creatinine', label: 'Serum Creatinine', unit: 'mg/dL', type: 'number', step: '0.01' },
    {
      key: 'gender', label: 'Gender', unit: '', type: 'select',
      options: [{ value: 'male', label: 'Male' }, { value: 'female', label: 'Female' }]
    },
  ],
  'egfr-mdrd': [
    { key: 'serumCreatinine', label: 'Serum Creatinine', unit: 'mg/dL', type: 'number', step: '0.01' },
    { key: 'age', label: 'Age', unit: 'years', type: 'number', step: '1' },
    {
      key: 'gender', label: 'Gender', unit: '', type: 'select',
      options: [{ value: 'male', label: 'Male' }, { value: 'female', label: 'Female' }]
    },
  ],
  'gfr': [
    { key: 'age', label: 'Age', unit: 'years', type: 'number', step: '1' },
    { key: 'creatinine', label: 'Serum Creatinine', unit: 'mg/dL', type: 'number', step: '0.01' },
    {
      key: 'gender', label: 'Gender', unit: '', type: 'select',
      options: [{ value: 'male', label: 'Male' }, { value: 'female', label: 'Female' }]
    },
  ],
  'map': [
    { key: 'systolic', label: 'Systolic BP', unit: 'mmHg', type: 'number', step: '1' },
    { key: 'diastolic', label: 'Diastolic BP', unit: 'mmHg', type: 'number', step: '1' },
  ],
  'shock-index': [
    { key: 'heartRate', label: 'Heart Rate', unit: 'bpm', type: 'number', step: '1' },
    { key: 'systolicBP', label: 'Systolic BP', unit: 'mmHg', type: 'number', step: '1' },
  ],
  'pf-ratio': [
    { key: 'paO2', label: 'PaO2', unit: 'mmHg', type: 'number', step: '1' },
    { key: 'fiO2', label: 'FiO2', unit: '0–1', type: 'number', step: '0.01' },
  ],
  'aa-gradient': [
    { key: 'pAO2', label: 'Alveolar O2 (PAO2)', unit: 'mmHg', type: 'number', step: '1' },
    { key: 'paO2', label: 'Arterial O2 (PaO2)', unit: 'mmHg', type: 'number', step: '1' },
  ],
  'bmi': [
    { key: 'weight', label: 'Weight', unit: 'kg', type: 'number', step: '0.1' },
    { key: 'height', label: 'Height', unit: 'cm', type: 'number', step: '0.1' },
  ],
  'anion-gap': [
    { key: 'sodium', label: 'Sodium (Na)', unit: 'mEq/L', type: 'number', step: '1' },
    { key: 'chloride', label: 'Chloride (Cl)', unit: 'mEq/L', type: 'number', step: '1' },
    { key: 'bicarbonate', label: 'Bicarbonate (HCO3)', unit: 'mEq/L', type: 'number', step: '1' },
  ],
  'serum-osmolality': [
    { key: 'sodium', label: 'Sodium (Na)', unit: 'mEq/L', type: 'number', step: '1' },
    { key: 'glucose', label: 'Glucose', unit: 'mg/dL', type: 'number', step: '1' },
    { key: 'bun', label: 'BUN', unit: 'mg/dL', type: 'number', step: '1' },
  ],
  'maintenance-fluid': [
    { key: 'weightKg', label: 'Weight', unit: 'kg', type: 'number', step: '0.1' },
  ],
  'fluid-deficit': [
    { key: 'weightKg', label: 'Weight', unit: 'kg', type: 'number', step: '0.1' },
    { key: 'dehydrationPercent', label: 'Dehydration', unit: '%', type: 'number', step: '1' },
  ],
  'bsa': [
    { key: 'weight', label: 'Weight', unit: 'kg', type: 'number', step: '0.1' },
    { key: 'height', label: 'Height', unit: 'cm', type: 'number', step: '0.1' },
  ],
  'bsa-dubois': [
    { key: 'weightKg', label: 'Weight', unit: 'kg', type: 'number', step: '0.1' },
    { key: 'heightCm', label: 'Height', unit: 'cm', type: 'number', step: '0.1' },
  ],
  'ibw': [
    { key: 'height', label: 'Height', unit: 'cm', type: 'number', step: '0.1' },
    {
      key: 'gender', label: 'Gender', unit: '', type: 'select',
      options: [{ value: 'male', label: 'Male' }, { value: 'female', label: 'Female' }]
    },
  ],

  'edd': [
    { key: 'lmp', label: 'Last Menstrual Period (LMP)', unit: '', type: 'date', step: null },
  ],

  'gestational-age': [
    { key: 'lmp', label: 'Last Menstrual Period (LMP)', unit: '', type: 'date', step: null },
  ],

  'trimester': [
    { key: 'gestationalAgeWeeks', label: 'Gestational Age', unit: 'weeks', type: 'number', step: '0.1' },
  ],

  // ── Fetal Growth ─────────────────────────────────────────────────────────────
  'efetal-weight': [
    { key: 'bpd', label: 'Biparietal Diameter (BPD)', unit: 'mm', type: 'number', step: '0.1' },
    { key: 'ac', label: 'Abdominal Circumference (AC)', unit: 'mm', type: 'number', step: '0.1' },
    { key: 'fl', label: 'Femur Length (FL)', unit: 'mm', type: 'number', step: '0.1' },
    { key: 'hc', label: 'Head Circumference (HC) — optional', unit: 'mm', type: 'number', step: '0.1' },
  ],

  'afi': [
    { key: 'q1', label: 'Quadrant 1 (Right Upper)', unit: 'cm', type: 'number', step: '0.1' },
    { key: 'q2', label: 'Quadrant 2 (Right Lower)', unit: 'cm', type: 'number', step: '0.1' },
    { key: 'q3', label: 'Quadrant 3 (Left Lower)', unit: 'cm', type: 'number', step: '0.1' },
    { key: 'q4', label: 'Quadrant 4 (Left Upper)', unit: 'cm', type: 'number', step: '0.1' },
  ],

  'fetal-percentile': [
    { key: 'gestationalAgeWeeks', label: 'Gestational Age', unit: 'weeks', type: 'number', step: '0.1' },
    { key: 'fetalWeight', label: 'Estimated Fetal Weight', unit: 'g', type: 'number', step: '1' },
  ],

  // ── Maternal Health ──────────────────────────────────────────────────────────
  'bmi-pregnancy': [
    { key: 'weightKg', label: 'Weight', unit: 'kg', type: 'number', step: '0.1' },
    { key: 'heightCM', label: 'Height', unit: 'cm', type: 'number', step: '1' },
  ],

  'pregnancy-weight-gain': [
    { key: 'bmi', label: 'Pre-pregnancy BMI', unit: 'kg/m²', type: 'number', step: '0.1' },
  ],

  // ── Risk & Screening ─────────────────────────────────────────────────────────
  'down-syndrome-risk': [
    { key: 'maternalAge', label: 'Maternal Age', unit: 'years', type: 'number', step: '1' },
    { key: 'ntValue', label: 'Nuchal Translucency (NT)', unit: 'mm', type: 'number', step: '0.1' },
  ],

  'preterm-risk': [
    { key: 'cervicalLength', label: 'Cervical Length', unit: 'mm', type: 'number', step: '0.1' },
    {
      key: 'previousPreterm', label: 'Previous Preterm Birth', unit: '', type: 'select',
      options: [{ value: '0', label: 'No' }, { value: '1', label: 'Yes' }]
    },
    {
      key: 'contractions', label: 'Regular Contractions', unit: '', type: 'select',
      options: [{ value: '0', label: 'No' }, { value: '1', label: 'Yes' }]
    },
  ],

  'gdm-risk': [
    { key: 'fastingGlucose', label: 'Fasting Glucose', unit: 'mg/dL', type: 'number', step: '1' },
    { key: 'postMealGlucose', label: 'Post-meal Glucose (2hr)', unit: 'mg/dL', type: 'number', step: '1' },
  ],

  // ── Labor & Delivery ─────────────────────────────────────────────────────────
  'bishop-score': [
    { key: 'dilation', label: 'Cervical Dilation', unit: 'cm', type: 'number', step: '0.5' },
    { key: 'effacement', label: 'Effacement', unit: '%', type: 'number', step: '5' },
    { key: 'station', label: 'Fetal Station (-3 to +3)', unit: '', type: 'number', step: '1' },
    {
      key: 'consistency', label: 'Cervical Consistency', unit: '', type: 'select',
      options: [
        { value: '0', label: 'Firm (0)' },
        { value: '1', label: 'Medium (1)' },
        { value: '2', label: 'Soft (2)' },
      ]
    },
    {
      key: 'position', label: 'Cervical Position', unit: '', type: 'select',
      options: [
        { value: '0', label: 'Posterior (0)' },
        { value: '1', label: 'Mid-position (1)' },
        { value: '2', label: 'Anterior (2)' },
      ]
    },
  ],

  'contraction-frequency': [
    { key: 'contractionsCount', label: 'Number of Contractions', unit: 'per 10 min', type: 'number', step: '1' },
  ],

  // ── Drug & Fluid ─────────────────────────────────────────────────────────────
  'oxytocin-rate': [
    { key: 'dose', label: 'Desired Dose', unit: 'mU/min', type: 'number', step: '0.5' },
    { key: 'concentration', label: 'Oxytocin Concentration', unit: 'mU/mL', type: 'number', step: '1' },
  ],

  'magsulfate-dose': [
    { key: 'patientWeight', label: 'Patient Weight', unit: 'kg', type: 'number', step: '0.1' },
  ],

  'iv-fluid-pregnancy': [
    { key: 'weightKg', label: 'Weight', unit: 'kg', type: 'number', step: '0.1' },
    { key: 'factor', label: 'Fluid Factor', unit: 'mL/kg', type: 'number', step: '1' },
  ],

  // ── Postpartum & Newborn ─────────────────────────────────────────────────────
  'apgar': [
    {
      key: 'appearance', label: 'Appearance / Color', unit: '', type: 'select',
      options: [
        { value: '0', label: '0 — Blue/Pale all over' },
        { value: '1', label: '1 — Blue extremities, pink body' },
        { value: '2', label: '2 — Completely pink' },
      ]
    },
    {
      key: 'pulse', label: 'Pulse / Heart Rate', unit: '', type: 'select',
      options: [
        { value: '0', label: '0 — Absent' },
        { value: '1', label: '1 — < 100 bpm' },
        { value: '2', label: '2 — ≥ 100 bpm' },
      ]
    },
    {
      key: 'grimace', label: 'Grimace / Reflex Irritability', unit: '', type: 'select',
      options: [
        { value: '0', label: '0 — No response' },
        { value: '1', label: '1 — Grimace' },
        { value: '2', label: '2 — Cry / Cough / Sneeze' },
      ]
    },
    {
      key: 'activity', label: 'Activity / Muscle Tone', unit: '', type: 'select',
      options: [
        { value: '0', label: '0 — Limp' },
        { value: '1', label: '1 — Some flexion' },
        { value: '2', label: '2 — Active motion' },
      ]
    },
    {
      key: 'respiration', label: 'Respiration', unit: '', type: 'select',
      options: [
        { value: '0', label: '0 — Absent' },
        { value: '1', label: '1 — Weak / Irregular' },
        { value: '2', label: '2 — Strong cry' },
      ]
    },
  ],

  'newborn-percentile': [
    { key: 'birthWeight', label: 'Birth Weight', unit: 'g', type: 'number', step: '1' },
    { key: 'gestationalAgeWeeks', label: 'Gestational Age at Birth', unit: 'weeks', type: 'number', step: '0.1' },
  ],

  'bilirubin-risk': [
    { key: 'bilirubin', label: 'Total Serum Bilirubin', unit: 'mg/dL', type: 'number', step: '0.1' },
    { key: 'ageHours', label: 'Newborn Age', unit: 'hours', type: 'number', step: '1' },
  ]
};

const DOSE_SLUGS = ['weight-based-dose', 'bsa-dose', 'loading-dose', 'maintenance-dose', 'infusion-rate'];

// ── Result Display ───────────────────────────────────────────────────────────
const ResultDisplay = ({ result }) => {
  const entries = Object.entries(result).filter(([k]) => k !== 'unit');
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {entries[0] && (
        <div style={{
          background: 'linear-gradient(135deg, #e8f0fe 0%, #fff 100%)',
          border: '2px solid #c7d9fc',
          borderRadius: 'var(--radius-lg)',
          padding: '28px',
          textAlign: 'center',
        }}>
          <div style={{ fontSize: '0.6875rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--primary)', marginBottom: 10 }}>
            {entries[0][0].replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 10 }}>
            <span style={{ fontSize: '3.5rem', fontWeight: 700, color: 'var(--primary-dark)', fontFamily: 'var(--font-mono)', lineHeight: 1 }}>
              {entries[0][1]}
            </span>
            {result.unit && (
              <span style={{ fontSize: '1.125rem', color: 'var(--gray-400)', fontWeight: 500 }}>
                {result.unit}
              </span>
            )}
          </div>
        </div>
      )}
      {entries.length > 1 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 10 }}>
          {entries.slice(1).map(([k, v]) => (
            <div key={k} style={{ background: '#fff', border: '1px solid var(--gray-100)', borderRadius: 'var(--radius)', padding: '12px 14px' }}>
              <div style={{ fontSize: '0.6875rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--gray-400)', marginBottom: 4 }}>
                {k.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </div>
              <div style={{ fontSize: '0.9375rem', fontWeight: 600, color: 'var(--gray-800)' }}>{v}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Calculator Panel (main right area) ───────────────────────────────────────
const CalculatorPanel = ({ calc, category, onBack }) => {
  const [inputs, setInputs] = useState({});
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [medSearch, setMedSearch] = useState('');
  const [medResults, setMedResults] = useState([]);
  const [selectedMed, setSelectedMed] = useState(null);
  const [dosageRule, setDosageRule] = useState(null);
  const [showDrop, setShowDrop] = useState(false);
  const [medLoading, setMedLoading] = useState(false);
  const dropRef = useRef(null);

  const fields = FIELD_DEFINITIONS[calc.slug] || [];
  const isDoseCalc = DOSE_SLUGS.includes(calc.slug);
  const CatIcon = category?.icon || Calculator;

  useEffect(() => {
    setInputs({}); setResult(null); setError('');
    setMedSearch(''); setSelectedMed(null); setDosageRule(null);
  }, [calc.slug]);

  useEffect(() => {
    const h = (e) => { if (dropRef.current && !dropRef.current.contains(e.target)) setShowDrop(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  useEffect(() => {
    if (!medSearch || selectedMed) { setMedResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const res = await medicineAPI.getAll({ search: medSearch });
        setMedResults(res.data.data.slice(0, 6));
        setShowDrop(true);
      } catch { }
    }, 300);
    return () => clearTimeout(t);
  }, [medSearch, selectedMed]);

  const handleMedSelect = async (med) => {
    setSelectedMed(med); setMedSearch(med.name);
    setShowDrop(false); setMedLoading(true);
    try {
      const res = await dosageAPI.getRules({ medicine_id: med.id });
      const rule = res.data.data[0];
      setDosageRule(rule || null);
      if (rule) {
        setInputs(prev => ({
          ...prev,
          dosePerKg: rule.dose_per_kg || '',
          dosePerM2: rule.dose_per_kg || '',
          totalDose: rule.max_dose || '',
        }));
      }
    } catch { }
    finally { setMedLoading(false); }
  };

  const clearMed = () => {
    setSelectedMed(null); setMedSearch('');
    setDosageRule(null); setInputs({});
  };

  const handleCalculate = async (e) => {
    e.preventDefault();
    setLoading(true); setError(''); setResult(null);
    try {
      const res = await calculatorAPI.calculate(calc.slug, inputs);
      setResult(res.data.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Calculation failed. Please check your inputs.');
    } finally { setLoading(false); }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100%' }}>

      {/* Header strip */}
      <div className="calc-panel-header" style={{
        padding: '28px 36px 24px',
        background: '#fff',
        borderBottom: '1px solid var(--gray-100)',
        position: 'sticky', top: 0, zIndex: 10,
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
          <button
            className="mobile-back-btn"
            onClick={onBack}
            style={{ background: 'none', border: 'none', padding: '0 4px', cursor: 'pointer', marginTop: 4, color: 'var(--gray-500)', flexShrink: 0 }}
          >
            <ArrowLeft size={22} />
          </button>
          <div style={{
            width: 52, height: 52, borderRadius: 14, flexShrink: 0,
            background: category?.bg || '#e8f0fe',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          }}>
            <CatIcon size={24} color={category?.color || 'var(--primary)'} strokeWidth={1.8} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '0.6875rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: category?.color || 'var(--primary)', marginBottom: 4 }}>
              {category?.label}
            </div>
            <h2 style={{ margin: 0, fontSize: '1.375rem', fontWeight: 700, color: 'var(--gray-900)', lineHeight: 1.2 }}>
              {calc.name}
            </h2>
            <p style={{ margin: '5px 0 0', fontSize: '0.875rem', color: 'var(--gray-400)', lineHeight: 1.5 }}>
              {calc.description}
            </p>
          </div>
        </div>

        {calc.formula_description && (
          <div style={{
            marginTop: 16, padding: '10px 14px',
            background: 'var(--gray-50)', borderRadius: 'var(--radius)',
            border: '1px solid var(--gray-200)',
            display: 'flex', alignItems: 'flex-start', gap: 8,
          }}>
            <Info size={14} color="var(--gray-400)" style={{ marginTop: 1, flexShrink: 0 }} />
            <span style={{ fontSize: '0.8125rem', fontFamily: 'var(--font-mono)', color: 'var(--gray-600)', lineHeight: 1.5 }}>
              {calc.formula_description}
            </span>
          </div>
        )}
      </div>

      {/* Body */}
      <div className="calc-panel-body" style={{ padding: '28px 36px 40px', flex: 1 }}>

        {/* Medicine linker for dose calculators */}
        {isDoseCalc && (
          <div style={{
            marginBottom: 24, padding: '16px 18px',
            background: '#f0f5ff', borderRadius: 'var(--radius)',
            border: '1px solid #c7d9fc',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 12, fontSize: '0.8125rem', fontWeight: 600, color: 'var(--primary-dark)' }}>
              <LinkIcon size={13} />
              Link to Medicine
              <span style={{ fontWeight: 400, color: 'var(--gray-400)', marginLeft: 2 }}>— auto-fills dose values from formulary</span>
            </div>
            <div style={{ position: 'relative' }} ref={dropRef}>
              <Search size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--gray-400)', pointerEvents: 'none' }} />
              <input
                className="form-input"
                style={{ paddingLeft: 36, background: '#fff' }}
                placeholder="Search and select a medicine…"
                value={medSearch}
                onChange={e => { setMedSearch(e.target.value); setSelectedMed(null); setDosageRule(null); }}
                autoComplete="off"
              />
              {showDrop && medResults.length > 0 && (
                <div className="medicine-dropdown">
                  {medResults.map(m => (
                    <div key={m.id} className="medicine-option" onMouseDown={() => handleMedSelect(m)}>
                      <div>{m.name}</div>
                      <div className="medicine-option-sub">{m.generic_name || m.category_name}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {medLoading && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 10, fontSize: '0.8125rem', color: 'var(--gray-400)' }}>
                <Loader2 size={13} style={{ animation: 'spin 0.6s linear infinite' }} />
                Loading dose rules…
              </div>
            )}

            {dosageRule && selectedMed && (
              <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5,
                  background: '#fff', border: '1px solid var(--primary-light)',
                  borderRadius: 100, padding: '4px 12px',
                  fontSize: '0.8125rem', fontWeight: 600, color: 'var(--primary-dark)',
                }}>
                  <CheckCircle size={12} color="var(--success)" /> {selectedMed.name}
                </span>
                <span className="badge badge-gray">{dosageRule.dose_per_kg} {dosageRule.unit}/kg</span>
                {dosageRule.max_dose && <span className="badge badge-gray">Max: {dosageRule.max_dose} {dosageRule.unit}</span>}
                {dosageRule.frequency && <span className="badge badge-gray">{dosageRule.frequency}</span>}
                <button type="button" onClick={clearMed}
                  style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray-400)', display: 'flex', alignItems: 'center', gap: 3, fontSize: '0.8125rem' }}>
                  <X size={12} /> Clear
                </button>
              </div>
            )}

            {selectedMed && !dosageRule && !medLoading && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 10, fontSize: '0.8125rem', color: 'var(--warning)' }}>
                <AlertTriangle size={13} />
                No dose rules found for this medicine. Enter values manually below.
              </div>
            )}
          </div>
        )}

        {/* Input Form */}
        {fields.length === 0 ? (
          <div className="alert alert-warning">
            <AlertTriangle size={16} />
            <div>Field definitions not yet configured for <strong>{calc.slug}</strong>.</div>
          </div>
        ) : (
          <form onSubmit={handleCalculate}>
            <div style={{ marginBottom: 12, fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--gray-400)' }}>
              Input Parameters
            </div>

            <div className="calc-input-row" style={{
              display: 'grid',
              gridTemplateColumns: fields.length === 1 ? '1fr' : '1fr 1fr',
              gap: 14,
              marginBottom: 22,
            }}>
              {fields.map(f => (
                <div className="form-group" key={f.key}>
                  <label className="form-label" style={{ fontSize: '0.875rem' }}>
                    {f.label}
                    {f.unit && <span style={{ color: 'var(--gray-400)', fontWeight: 400, marginLeft: 4, fontSize: '0.8rem' }}>({f.unit})</span>}
                  </label>
                  {f.type === 'select' ? (
                    <select
                      className="form-select"
                      value={inputs[f.key] || ''}
                      onChange={e => setInputs(p => ({ ...p, [f.key]: e.target.value }))}
                      required
                    >
                      <option value="">— Select —</option>
                      {f.options.map(o => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  ) : (
                    <div style={{ position: 'relative' }}>
                      <input
                        className="form-input"
                        type={f.type || 'number'}
                        step={f.step || undefined}
                        min={(!f.type || f.type === 'number') ? "0" : undefined}
                        placeholder={f.type === 'date' ? '' : '0'}
                        value={inputs[f.key] || ''}
                        onChange={e => setInputs(p => ({ ...p, [f.key]: e.target.value }))}
                        style={f.unit ? { paddingRight: 56 } : {}}
                        required
                      />
                      {f.unit && (
                        <span style={{
                          position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                          fontSize: '0.75rem', color: 'var(--gray-400)', pointerEvents: 'none',
                          fontFamily: 'var(--font-mono)', userSelect: 'none',
                        }}>
                          {f.unit}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {error && (
              <div className="alert alert-danger" style={{ marginBottom: 16 }}>
                <AlertTriangle size={15} />
                <div>{error}</div>
              </div>
            )}

            <div style={{ display: 'flex', gap: 10 }}>
              <button
                type="submit"
                className="btn btn-primary btn-lg"
                style={{ flex: 1, justifyContent: 'center' }}
                disabled={loading}
              >
                {loading
                  ? <><Loader2 size={16} style={{ animation: 'spin 0.6s linear infinite' }} /> Calculating…</>
                  : <><Calculator size={16} /> Calculate</>
                }
              </button>
              {(result || Object.keys(inputs).some(k => inputs[k])) && (
                <button
                  type="button"
                  className="btn btn-secondary btn-lg btn-icon"
                  onClick={() => { setInputs({}); setResult(null); setError(''); }}
                  title="Reset form"
                >
                  <X size={16} />
                </button>
              )}
            </div>
          </form>
        )}

        {/* Result panel */}
        {result && (
          <div style={{ marginTop: 32 }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 6,
              marginBottom: 14, fontSize: '0.75rem', fontWeight: 700,
              textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--success)',
            }}>
              <CheckCircle size={14} />
              Result
              {selectedMed && (
                <span style={{ color: 'var(--primary)', fontWeight: 500, textTransform: 'none', letterSpacing: 0, marginLeft: 4 }}>
                  — {selectedMed.name}
                </span>
              )}
            </div>
            <ResultDisplay result={result} />

            {dosageRule && (
              <div style={{
                marginTop: 14, padding: '10px 14px',
                background: 'var(--gray-50)', borderRadius: 'var(--radius)',
                border: '1px solid var(--gray-100)',
                display: 'flex', alignItems: 'center', gap: 7,
                fontSize: '0.8125rem', color: 'var(--gray-500)',
              }}>
                <Info size={12} color="var(--gray-400)" />
                Based on dose rule: <strong style={{ color: 'var(--gray-700)' }}>{dosageRule.dose_per_kg} {dosageRule.unit}/kg</strong>
                {dosageRule.frequency && <> · {dosageRule.frequency}</>}
                {dosageRule.max_dose && <> · Max: {dosageRule.max_dose} {dosageRule.unit}</>}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ── Welcome / empty state ────────────────────────────────────────────────────
const WelcomeScreen = ({ totalCount, categoryCount }) => (
  <div style={{
    display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center',
    height: '100%', padding: 48, textAlign: 'center',
  }}>
    <div style={{
      width: 88, height: 88, borderRadius: 22,
      background: 'var(--primary-light)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      marginBottom: 20,
      boxShadow: '0 4px 16px rgba(26,110,245,0.12)',
    }}>
      <Calculator size={40} color="var(--primary)" strokeWidth={1.5} />
    </div>
    <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 700, color: 'var(--gray-800)', marginBottom: 8 }}>
      Select a Calculator
    </h3>
    <p style={{ color: 'var(--gray-400)', fontSize: '0.9375rem', maxWidth: 340, lineHeight: 1.7, margin: 0 }}>
      Choose from <strong style={{ color: 'var(--gray-600)' }}>{totalCount} clinical calculators</strong> across{' '}
      <strong style={{ color: 'var(--gray-600)' }}>{categoryCount} categories</strong> from the navigation panel on the left.
    </p>
    <div style={{ display: 'flex', gap: 8, marginTop: 24, flexWrap: 'wrap', justifyContent: 'center' }}>
      {['Weight Based Dose', 'BMI', 'Creatinine Clearance', 'Anion Gap', 'MAP'].map(n => (
        <span key={n} className="badge badge-primary" style={{ fontSize: '0.8125rem', padding: '5px 12px' }}>{n}</span>
      ))}
    </div>
  </div>
);

// ── Main export ──────────────────────────────────────────────────────────────
export default function MedicalCalculators() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [dbCalcs, setDbCalcs] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [activeSlug, setActiveSlug] = useState(null);

  useEffect(() => {
    calculatorAPI.getAll()
      .then(res => setDbCalcs(res.data.data || []))
      .catch(() => { })
      .finally(() => setFetching(false));
  }, []);

  const visibleCategories = CATEGORIES.map(cat => ({
    ...cat,
    calculators: cat.slugs.map(slug => dbCalcs.find(c => c.slug === slug)).filter(Boolean),
  })).filter(cat => cat.calculators.length > 0);

  const activeCalc = dbCalcs.find(c => c.slug === activeSlug) || null;
  const activeCategory = visibleCategories.find(cat => cat.slugs.includes(activeSlug)) || null;
  const totalCount = visibleCategories.reduce((sum, c) => sum + c.calculators.length, 0);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden', background: 'var(--gray-50)' }}>

      {/* ── Topbar ────────────────────────────────────────────────────────── */}
      <header style={{
        height: 60, background: '#fff',
        borderBottom: '1px solid var(--gray-100)',
        display: 'flex', alignItems: 'center',
        padding: '0 20px', gap: 12, flexShrink: 0, zIndex: 100,
        boxShadow: 'var(--shadow-xs)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 32, height: 32, background: 'var(--primary)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Pill size={17} color="#fff" strokeWidth={2} />
          </div>
          <span style={{ fontWeight: 700, fontSize: '1.0625rem', letterSpacing: '-0.02em', color: 'var(--gray-900)' }}>MediDose</span>
          <span style={{ color: 'var(--gray-300)' }}>·</span>
          <span style={{ fontSize: '0.875rem', color: 'var(--gray-500)', fontWeight: 500 }}>Medical Calculators</span>
        </div>
        <div style={{ flex: 1 }} />
        <button className="btn btn-secondary btn-sm" style={{ display: 'flex', alignItems: 'center', gap: 6 }} onClick={() => navigate('/calculator')}>
          <ArrowLeft size={14} /> Dose Calc
        </button>
        {user?.roles?.includes('admin') && (
          <button className="btn btn-secondary btn-sm" onClick={() => navigate('/admin')}>
            <LayoutDashboard size={16} style={{ marginRight: 6 }} /> Admin Panel
          </button>
        )}
        <button className="btn btn-ghost btn-sm" onClick={() => { logout(); navigate('/login'); }}>Sign out</button>
      </header>

      {/* ── Two-panel layout ──────────────────────────────────────────────── */}
      <div className="medical-calc-container" style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        <style>{`
          .mobile-back-btn { display: none; }
          @media (max-width: 768px) {
            .medical-calc-container { flex-direction: column; }
            .medical-calc-sidebar { width: 100% !important; border-right: none !important; flex: 1 !important; }
            .medical-calc-main { width: 100% !important; }
            .hide-on-mobile-if-active { display: none !important; }
            .mobile-back-btn { display: block; }
            .calc-panel-header { padding: 16px 20px !important; }
            .calc-panel-body { padding: 20px 20px 30px !important; }
            .calc-input-row { grid-template-columns: 1fr !important; }
          }
        `}</style>

        {/* ── Left sidebar nav ──────────────────────────────────────────── */}
        <aside className={`medical-calc-sidebar ${activeCalc ? 'hide-on-mobile-if-active' : ''}`} style={{
          width: 272, flexShrink: 0,
          background: '#fff',
          borderRight: '1px solid var(--gray-100)',
          display: 'flex', flexDirection: 'column',
          overflowY: 'auto',
        }}>
          {/* Sidebar header */}
          <div style={{ padding: '16px 16px 12px', borderBottom: '1px solid var(--gray-100)', background: '#fff', position: 'sticky', top: 0, zIndex: 5 }}>
            <div style={{ fontSize: '0.6875rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--gray-400)' }}>
              Clinical Calculators
            </div>
            <div style={{ fontSize: '0.8125rem', color: 'var(--gray-500)', marginTop: 2 }}>
              {fetching ? 'Loading…' : `${totalCount} tools · ${visibleCategories.length} categories`}
            </div>
          </div>

          {fetching ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
              <Loader2 size={22} color="var(--primary)" style={{ animation: 'spin 0.6s linear infinite' }} />
            </div>
          ) : (
            <nav style={{ padding: '6px 0 16px' }}>
              {visibleCategories.map((cat, ci) => {
                const Icon = cat.icon;
                const isCatActive = cat.calculators.some(c => c.slug === activeSlug);
                return (
                  <div key={cat.label} style={{ marginTop: ci > 0 ? 4 : 0 }}>
                    {/* Category label */}
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      padding: '10px 14px 6px',
                    }}>
                      <div style={{
                        width: 26, height: 26, borderRadius: 7,
                        background: cat.bg,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0,
                      }}>
                        <Icon size={14} color={cat.color} strokeWidth={2} />
                      </div>
                      <span style={{
                        fontSize: '0.75rem', fontWeight: 700,
                        textTransform: 'uppercase', letterSpacing: '0.05em',
                        color: isCatActive ? cat.color : 'var(--gray-500)',
                        flex: 1,
                      }}>
                        {cat.label}
                      </span>
                      <span style={{
                        fontSize: '0.6875rem', background: cat.bg,
                        color: cat.color, borderRadius: 100,
                        padding: '1px 7px', fontWeight: 700,
                      }}>
                        {cat.calculators.length}
                      </span>
                    </div>

                    {/* Calculator nav items */}
                    {cat.calculators.map(calc => {
                      const isActive = activeSlug === calc.slug;
                      return (
                        <button
                          key={calc.slug}
                          onClick={() => setActiveSlug(calc.slug)}
                          style={{
                            width: '100%', textAlign: 'left',
                            padding: '8px 14px 8px 48px',
                            background: isActive ? cat.bg : 'transparent',
                            border: 'none',
                            borderLeft: `3px solid ${isActive ? cat.color : 'transparent'}`,
                            cursor: 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            gap: 6, transition: 'background 0.1s, border-color 0.1s',
                          }}
                          onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = 'var(--gray-50)'; }}
                          onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
                        >
                          <span style={{
                            fontSize: '0.875rem', lineHeight: 1.35,
                            fontWeight: isActive ? 600 : 400,
                            color: isActive ? cat.color : 'var(--gray-600)',
                          }}>
                            {calc.name}
                          </span>
                          {isActive && <ChevronRight size={13} color={cat.color} style={{ flexShrink: 0 }} />}
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </nav>
          )}
        </aside>

        {/* ── Main panel ───────────────────────────────────────────────── */}
        <main className={`medical-calc-main ${!activeCalc ? 'hide-on-mobile-if-active' : ''}`} style={{ flex: 1, overflowY: 'auto', background: 'var(--gray-50)' }}>
          {!activeCalc ? (
            <WelcomeScreen totalCount={totalCount} categoryCount={visibleCategories.length} />
          ) : (
            <div style={{
              maxWidth: 700, margin: '0 auto',
              background: '#fff',
              minHeight: '100%',
              boxShadow: '0 0 0 1px var(--gray-100)',
            }}>
              <CalculatorPanel
                key={activeCalc.slug}
                calc={activeCalc}
                category={activeCategory}
                onBack={() => setActiveSlug(null)}
              />
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
